import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/shared/hooks/usePermissions";
import type { Module } from "@/config/roles";

interface SetupTile {
  to: string;
  titleKey: string;
  descKey: string;
  icon: string;
  /** Module for canRead check, or special role name for write-only standalone tiles. */
  guard: Module | "chat_template" | "upload_user_data";
}

const tiles: SetupTile[] = [
  {
    to: "/companies",
    titleKey: "company.title",
    descKey: "other_setup.tiles.companies_desc",
    icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
    guard: "company",
  },
  {
    to: "/sending-orgs",
    titleKey: "sending_org.title",
    descKey: "other_setup.tiles.sending_orgs_desc",
    icon: "M12 19l9 2-9-18-9 18 9-2zm0 0v-8",
    guard: "company",
  },
  {
    to: "/issue-types",
    titleKey: "issue_type.title",
    descKey: "other_setup.tiles.issue_types_desc",
    icon: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z",
    guard: "visa",
  },
  {
    to: "/visa-list",
    titleKey: "visa_list.title",
    descKey: "other_setup.tiles.visa_list_desc",
    icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
    guard: "visa",
  },
  {
    to: "/chat-templates",
    titleKey: "chat_templates.title",
    descKey: "other_setup.tiles.chat_templates_desc",
    icon: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z",
    guard: "chat_template",
  },
  {
    to: "/upload-profile-visa",
    titleKey: "upload_profile_visa.title",
    descKey: "other_setup.tiles.upload_profile_visa_desc",
    icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12",
    guard: "upload_user_data",
  },
  {
    to: "/assessments",
    titleKey: "assessment.title",
    descKey: "other_setup.tiles.assessments_desc",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
    guard: "assessments",
  },
];

export const OtherSetupPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canRead, isFullAccess, roles } = usePermissions();

  const isVisible = (guard: SetupTile["guard"]): boolean => {
    if (guard === "chat_template")   return isFullAccess || roles.includes("chat_template_write");
    if (guard === "upload_user_data") return isFullAccess || roles.includes("upload_user_data_write");
    return canRead(guard);
  };

  const visibleTiles = tiles.filter((tile) => isVisible(tile.guard));

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("other_setup.title")}</h1>
        <p className="text-sm text-gray-500 mt-1">{t("other_setup.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {visibleTiles.map((tile) => (
          <button
            key={tile.to}
            onClick={() => navigate(tile.to)}
            className="flex items-start gap-4 p-5 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-indigo-300 hover:shadow-md transition-all text-left group"
          >
            <div className="flex-shrink-0 w-11 h-11 bg-indigo-50 rounded-lg flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
              <svg
                className="w-6 h-6 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={tile.icon} />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors">
                {t(tile.titleKey)}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">{t(tile.descKey)}</p>
            </div>
            <svg
              className="ml-auto flex-shrink-0 w-5 h-5 text-gray-300 group-hover:text-indigo-400 transition-colors self-center"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
};
